import {
  AddAssetTagsResponseData,
  ApiFetchDataResponse,
  ApiListDataResponse,
  AttachmentInputDto,
  BulkDownloadAssetsDto,
  CustomFieldKeyResponseData,
  CustomFieldKeyValueDto,
  GenericFileDto,
} from '@integration-frontends/common/brandfolder-api';
import {
  Asset,
  AssetDetails,
  AssetIdResults,
  AssetIdsResultSetBase,
  AssetsListResultSetBase,
  AssetsListResultSet,
  IAssetRepo,
  ListOptions,
  PagedResults,
  Section,
  AssetCountResults,
  AssetsCountResultSetBase,
  CollectionStorageResults,
  CollectionStorageResultSetBase,
} from '..';
import { Container } from '../../container';
import {
  assetCustomFieldValuesMockData,
  assetsMockData,
  assetTagsMockData,
  attachmentsMockData,
  listCustomFieldKeysResponseMockData,
  mockAssetUploadResponse,
} from './data';

export class AssetRepoMock implements IAssetRepo {
  create(
    container: Container,
    sectionId: string,
    name: string,
    files: File[],
  ): Promise<ApiListDataResponse> {
    return Promise.resolve(undefined);
  }

  uppyUpload(
    sectionId: string,
    name: string,
    attachment: AttachmentInputDto,
    collectionId?: string,
  ): Promise<ApiListDataResponse> {
    return Promise.resolve(mockAssetUploadResponse);
  }

  createExternalMedia(
    container: Container,
    sectionId: string,
    source: string,
    externalMedia: { name: string; url: string },
  ): Promise<ApiFetchDataResponse<GenericFileDto>> {
    return Promise.resolve(undefined);
  }

  bulkAddAssetTags(
    assetIds: string[],
    tags: string[],
    collectionId?: string,
    locale?: string,
    source?: string,
  ): Promise<AddAssetTagsResponseData[]> {
    return Promise.resolve(undefined);
  }

  addAssetTags(assetIds: string, tags: string[]): Promise<AddAssetTagsResponseData> {
    return;
  }

  listCustomFieldKeys(container: Container): Promise<CustomFieldKeyResponseData> {
    return Promise.resolve(listCustomFieldKeysResponseMockData);
  }

  addAssetCustomFields(
    assetIds: string[],
    container: Container,
    customFields: CustomFieldKeyValueDto[],
  ): Promise<void> {
    return Promise.resolve(undefined);
  }

  fetchAsset(assetId: string): Promise<Asset> {
    return Promise.resolve(assetsMockData[0]);
  }

  getAssetDetails(assetId: string): Promise<AssetDetails> {
    return Promise.resolve({
      tags: assetTagsMockData,
      customFieldValues: assetCustomFieldValuesMockData,
    });
  }

  listBrandfolderAssets(
    brandfolderId: string,
    options: ListOptions | undefined,
  ): Promise<AssetsListResultSet> {
    return Promise.resolve({
      assets: assetsMockData,
      attachments: attachmentsMockData,
    });
  }

  listCollectionAssets(
    collectionId: string,
    options: ListOptions | undefined,
  ): Promise<AssetsListResultSet> {
    return Promise.resolve({
      assets: assetsMockData,
      attachments: attachmentsMockData,
    });
  }

  listCollectionSectionAssets(
    collectionId: string,
    sectionId: string,
    options: ListOptions | undefined,
  ): Promise<PagedResults<AssetsListResultSet>> {
    return Promise.resolve(undefined);
  }

  listContainerSectionAssets(
    container: Container,
    sectionId: string,
    options: ListOptions | undefined,
  ): Promise<PagedResults<AssetsListResultSet>> {
    return Promise.resolve({
      data: {
        assets: assetsMockData,
        attachments: attachmentsMockData,
      },
      totalCount: assetsMockData.length,
      totalPages: Math.ceil(assetsMockData.length / 48),
      perPage: assetsMockData.length,
      currentPage: options.pagination.page || 1,
      nextPage: options.pagination.nextPage || null,
    });
  }

  listContainerSectionsAssets(
    container: Container,
    sections: Section[],
    options: ListOptions | undefined,
  ): Promise<{ sectionId: string; results: PagedResults<AssetsListResultSet> }[]> {
    return Promise.resolve([]);
  }

  listSectionAssets(
    sectionId: string,
    options: ListOptions | undefined,
  ): Promise<PagedResults<AssetsListResultSet>> {
    return Promise.resolve(undefined);
  }

  listContainerAssets(
    container: Container,
    options: ListOptions | undefined,
  ): Promise<PagedResults<AssetsListResultSet>> {
    return Promise.resolve({
      data: {
        assets: assetsMockData,
        attachments: attachmentsMockData,
      },
      totalCount: assetsMockData.length,
      totalPages: Math.ceil(assetsMockData.length / 48),
      perPage: assetsMockData.length,
      currentPage: options.pagination.page || 1,
      nextPage: options.pagination.nextPage || null,
      recordCount: assetsMockData.length,
    });
  }

  listContainerAssetsBase(
    container: Container,
    options: ListOptions | undefined,
  ): Promise<PagedResults<AssetsListResultSet>> {
    return Promise.resolve({
      data: {
        assets: assetsMockData,
        attachments: attachmentsMockData,
      },
      totalCount: assetsMockData.length,
      totalPages: Math.ceil(assetsMockData.length / 48),
      perPage: assetsMockData.length,
      currentPage: options.pagination.page || 1,
      nextPage: options.pagination.nextPage || null,
      recordCount: assetsMockData.length,
    });
  }

  listContainerAssetIdsBase(
    container: Container,
    options: ListOptions | undefined,
  ): Promise<AssetIdResults<AssetIdsResultSetBase>> {
    return Promise.resolve({
      data: {
        assetIds: assetsMockData.map((asset) => asset.id),
      },
      recordCount: assetsMockData.length,
    });
  }

  listCollectionAssetsCountBase(
    collectionId: string,
  ): Promise<AssetCountResults<AssetsCountResultSetBase>> {
    return Promise.resolve({
      data: {
        count: assetsMockData.length,
      },
      cacheDurationSeconds: 60,
      limitReached: false,
    });
  }

  listCollectionStorageRemainingBase(
    collectionId: string,
  ): Promise<CollectionStorageResults<CollectionStorageResultSetBase>> {
    return Promise.resolve({
      data: {
        remaining: 107374179600,
      },
      storageLimit: 107374182400,
    });
  }

  bulkDownloadAssets(assetIds: string[], name?: string): Promise<BulkDownloadAssetsDto> {
    return Promise.resolve({
      data: {
        attachment_count: '0',
        manifest_digest: '',
        redirect_url: '',
      },
    });
  }
}

export function createFakeAssetsResponse(
  overrides: Partial<PagedResults<AssetsListResultSet>>,
): PagedResults<AssetsListResultSet> {
  return {
    data: {
      assets: assetsMockData,
      attachments: attachmentsMockData,
    },
    totalCount: overrides?.data?.assets?.length || assetsMockData.length,
    totalPages: Math.ceil(assetsMockData.length / 48),
    perPage: overrides?.data?.assets?.length || assetsMockData.length,
    currentPage: 1,
    nextPage: null,
    recordCount: assetsMockData.length,
    ...overrides,
  };
}

export function createFakeAssetsBaseResponse(
  overrides: Partial<PagedResults<AssetsListResultSetBase>>,
): PagedResults<AssetsListResultSetBase> {
  return {
    data: {
      assets: assetsMockData,
    },
    totalCount: overrides?.data?.assets?.length || assetsMockData.length,
    totalPages: Math.ceil(assetsMockData.length / 48),
    perPage: overrides?.data?.assets?.length || assetsMockData.length,
    currentPage: 1,
    nextPage: null,
    recordCount: assetsMockData.length,
    ...overrides,
  };
}

export function createFakeAssetIdsBaseResponse(
  overrides: Partial<AssetIdResults<AssetIdsResultSetBase>>,
): AssetIdResults<AssetIdsResultSetBase> {
  return {
    data: {
      assetIds: assetsMockData.map((asset) => asset.id),
    },
    recordCount: assetsMockData.length,
    ...overrides,
  };
}
